import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import {
  Button,
  Flex,
  Form,
  FormListFieldData,
  Input,
  Select,
  SelectProps,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import {
  getDowntimeProps,
  getFrequencyProps,
  getPriorityProps,
  getRestrictionProps,
} from "../../../../../tickets";
import { SaveConfig } from "../../../types/Config";
import { CustomLabel } from "../../../types/field_configs/Priority";

type Props = {
  field: FormListFieldData;
  onRemove: () => void;
};

export const CustomLabelSelect = observer((props: Props) => {
  const t = useTranslation();
  const { labels: priorityLabels } = getPriorityProps();
  const { labels: restrictionLabels } = getRestrictionProps();
  const { labels: frequencyLabels } = getFrequencyProps();
  const { labels: downtimeLabels } = getDowntimeProps();

  const customLabels = useWatch<SaveConfig, Array<CustomLabel>>(
    (select) => select.config?.find((c) => c.name === "priority")?.customLabels
  );

  const label = useWatch<SaveConfig, string>(
    (select) =>
      select.config?.find((c) => c.name === "priority")?.customLabels?.[
        props.field.name
      ]?.label
  );

  const options: SelectProps["options"] = useMemo(() => {
    return [
      {
        label: t("maintenance:ticket.create.priority.label"),
        options: priorityLabels.filter(filterLabel).map(mapLabel),
      },
      {
        label: t("maintenance:ticket.create.priority.restriction.label"),
        options: restrictionLabels.filter(filterLabel).map(mapLabel),
      },
      {
        label: t("maintenance:ticket.create.priority.frequency.label"),
        options: frequencyLabels.filter(filterLabel).map(mapLabel),
      },
      {
        label: t("maintenance:ticket.create.priority.downtime.label"),
        options: downtimeLabels.filter(filterLabel).map(mapLabel),
      },
    ];
  }, [customLabels]);

  // the option value is the label of the custom label
  function mapLabel(currentLabel: string) {
    return {
      label: t(currentLabel),
      value: currentLabel,
    };
  }

  function filterLabel(currentLabel: string) {
    // the option value is the label of the custom label
    const isCurrentLabel = currentLabel === label;
    // check if the label is already in the custom labels
    const isLabelInCustomLabels = customLabels?.some(
      (customLabel) => customLabel?.label === currentLabel
    );
    return isCurrentLabel || !isLabelInCustomLabels;
  }

  // we use the option group label as prefix to clarify which label is selected
  const prefix = useMemo(() => {
    if (priorityLabels.some((l) => l === label))
      return t("maintenance:ticket.create.priority.label") + ":";
    if (restrictionLabels.some((l) => l === label))
      return t("maintenance:ticket.create.priority.restriction.label") + ":";
    if (frequencyLabels.some((l) => l === label))
      return t("maintenance:ticket.create.priority.frequency.label") + ":";
    if (downtimeLabels.some((l) => l === label))
      return t("maintenance:ticket.create.priority.downtime.label") + ":";

    return undefined;
  }, [label]);

  return (
    <Flex vertical gap="8px">
      <Flex gap="small" align="center">
        <Form.Item
          name={[props.field.name, "label"]}
          noStyle
          style={{ width: "100%" }}
        >
          <Select options={options} style={{ width: "100%" }} prefix={prefix} />
        </Form.Item>
        <Button
          danger
          icon={<Icon icon="fa:trash" />}
          onClick={props.onRemove}
        />
      </Flex>
      <Form.Item
        name={[props.field.name, "value"]}
        noStyle
        style={{ width: "100%" }}
      >
        <Input
          placeholder={t(
            "maintenance:ticket.create.config.priority.customLabels.value.placeholder"
          )}
        />
      </Form.Item>
    </Flex>
  );
});
